import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Trophy, Medal, Award, Skull, Target, Gamepad2, Swords, Clock, Shield, Zap, Users, History, Crown, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const RANK_COLORS = { 1: '#FFD700', 2: '#C0C0C0', 3: '#CD7F32' };

const GAME_MODES = [
  { id: 'classic', name: 'Classic', icon: Gamepad2, color: '#14F195', description: 'Single player campaign' },
  { id: 'coop_campaign', name: 'Co-op', icon: Users, color: '#9945FF', description: 'Team campaign' },
  { id: 'survival', name: 'Survival', icon: Skull, color: '#FF3B30', description: 'Endless waves' },
  { id: 'versus_score', name: 'Score Attack', icon: Trophy, color: '#FFD700', description: 'Compete for points' },
  { id: 'boss_rush', name: 'Boss Rush', icon: Swords, color: '#FF6B00', description: 'Boss after boss' },
  { id: 'time_attack', name: 'Time Attack', icon: Clock, color: '#00BFFF', description: 'Race the clock' },
  { id: 'last_stand', name: 'Last Stand', icon: Shield, color: '#8B5CF6', description: 'One life each' },
];

const VIEWS = [
  { id: 'leaderboard', name: 'Leaderboard', icon: Trophy, color: '#FFD700' },
  { id: 'history', name: 'History', icon: History, color: '#5AC8FA' },
  { id: 'champions', name: 'Champions', icon: Crown, color: '#FF9500' },
  { id: 'stats', name: 'Stats', icon: TrendingUp, color: '#14F195' },
];

const formatAddress = (address) => {
  if (!address) return 'Anonymous';
  if (address.startsWith('guest_')) return address.slice(0, 12);
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const Leaderboard = ({ scores: initialScores = [], onBack, currentWallet, onViewProfile }) => {
  const [activeView, setActiveView] = useState('leaderboard');
  const [selectedMode, setSelectedMode] = useState('classic');
  const [scores, setScores] = useState(initialScores);
  const [loading, setLoading] = useState(false);
  const [historyScores, setHistoryScores] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [champions, setChampions] = useState([]);
  const [modeStats, setModeStats] = useState([]);
  const [overallStats, setOverallStats] = useState({});

  const currentModeConfig = GAME_MODES.find(m => m.id === selectedMode) || GAME_MODES[0];

  // Fetch leaderboard
  useEffect(() => {
    if (activeView !== 'leaderboard') return;
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/game/leaderboard?limit=50&game_mode=${selectedMode}`);
        setScores(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [selectedMode, activeView]);

  // Fetch history
  const fetchHistory = useCallback(async (mode, offset) => {
    setLoading(true);
    try {
      const modeParam = mode === 'all' ? '' : `&game_mode=${mode}`;
      const res = await axios.get(`${API}/api/leaderboard/history?limit=30&offset=${offset}${modeParam}`);
      setHistoryScores(res.data.scores || []);
      setHistoryTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (activeView === 'history') fetchHistory(selectedMode, historyOffset);
  }, [activeView, selectedMode, historyOffset, fetchHistory]);

  // Fetch champions
  useEffect(() => {
    if (activeView !== 'champions') return;
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/leaderboard/champions?limit=20`);
        setChampions(res.data.champions || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [activeView]);

  // Fetch stats
  useEffect(() => {
    if (activeView !== 'stats') return;
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/leaderboard/stats`);
        setModeStats(res.data.mode_stats || []);
        setOverallStats(res.data.overall || {});
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [activeView]);

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Button onClick={onBack} variant="ghost" className="text-[#A1A1AA] hover:text-white" data-testid="leaderboard-back-btn">
            <ArrowLeft className="w-5 h-5 mr-2" /> Back
          </Button>
        </div>

        <div className="text-center mb-6">
          <Trophy className="w-12 h-12 text-[#FFD700] mx-auto mb-3" />
          <h1 className="font-game text-3xl md:text-4xl text-[#14F195]">LEADERBOARD</h1>
        </div>

        {/* View Tabs */}
        <div className="flex gap-2 justify-center mb-6">
          {VIEWS.map(v => (
            <button key={v.id} onClick={() => { setActiveView(v.id); setHistoryOffset(0); }}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-game text-xs transition-all ${
                activeView === v.id ? 'scale-105' : 'bg-[#111] border border-[#27272a] hover:bg-[#1a1a1a]'
              }`}
              style={{
                backgroundColor: activeView === v.id ? `${v.color}20` : undefined,
                borderColor: activeView === v.id ? v.color : undefined,
                color: activeView === v.id ? v.color : '#A1A1AA',
                border: activeView === v.id ? `2px solid ${v.color}` : undefined
              }}
              data-testid={`view-tab-${v.id}`}>
              <v.icon className="w-4 h-4" /> {v.name}
            </button>
          ))}
        </div>

        {/* Game Mode Selector (for leaderboard & history) */}
        {(activeView === 'leaderboard' || activeView === 'history') && (
          <div className="mb-6">
            <div className="flex flex-wrap gap-2 justify-center">
              {activeView === 'history' && (
                <button onClick={() => setSelectedMode('all')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-game text-xs transition-all ${
                    selectedMode === 'all' ? 'bg-white/10 border-2 border-white scale-105' : 'bg-[#111] border border-[#27272a]'
                  }`}
                  style={{ color: selectedMode === 'all' ? '#fff' : '#A1A1AA' }}>
                  All Modes
                </button>
              )}
              {GAME_MODES.map(mode => {
                const IconComp = mode.icon;
                const isActive = selectedMode === mode.id;
                return (
                  <button key={mode.id} onClick={() => setSelectedMode(mode.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-game text-xs transition-all ${
                      isActive ? 'scale-105' : 'bg-[#111] border border-[#27272a] hover:bg-[#1a1a1a]'
                    }`}
                    style={{
                      backgroundColor: isActive ? `${mode.color}20` : undefined,
                      borderColor: isActive ? mode.color : undefined,
                      color: isActive ? mode.color : '#A1A1AA',
                      border: isActive ? `2px solid ${mode.color}` : undefined
                    }}
                    data-testid={`leaderboard-tab-${mode.id}`}>
                    <IconComp className="w-4 h-4" />
                    <span className="hidden sm:inline">{mode.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="p-8 text-center">
            <Zap className="w-12 h-12 mx-auto mb-4 animate-pulse text-[#FFD700]" />
            <p className="text-[#A1A1AA]">Loading...</p>
          </div>
        )}

        {/* === LEADERBOARD VIEW === */}
        {!loading && activeView === 'leaderboard' && (
          <div className="glass-card rounded-xl overflow-hidden" style={{ borderColor: `${currentModeConfig.color}40` }}>
            <div className="grid grid-cols-12 gap-2 p-4 border-b border-[#27272a]" style={{ backgroundColor: `${currentModeConfig.color}10` }}>
              <div className="col-span-1 text-[#A1A1AA] font-game text-xs">#</div>
              <div className="col-span-4 text-[#A1A1AA] font-game text-xs">PLAYER</div>
              <div className="col-span-3 text-[#A1A1AA] font-game text-xs text-right">SCORE</div>
              <div className="col-span-2 text-[#A1A1AA] font-game text-xs text-right">LVL</div>
              <div className="col-span-2 text-[#A1A1AA] font-game text-xs text-right">KILLS</div>
            </div>
            <ScrollArea className="h-[400px]">
              {scores.length === 0 ? (
                <div className="p-8 text-center">
                  <Skull className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                  <p className="text-[#A1A1AA]">No scores yet for {currentModeConfig.name}.</p>
                </div>
              ) : scores.map((score, index) => {
                const rank = index + 1;
                const isMe = score.wallet_address === currentWallet;
                const rc = RANK_COLORS[rank] || '#A1A1AA';
                return (
                  <div key={score.id || index} className={`grid grid-cols-12 gap-2 p-4 border-b border-[#1a1a1a] ${isMe ? 'bg-[#14F195]/10' : 'hover:bg-[#111]'}`}
                    data-testid={`leaderboard-row-${rank}`}>
                    <div className="col-span-1 flex items-center">
                      {rank === 1 ? <Trophy className="w-5 h-5" style={{ color: rc }} /> :
                       rank === 2 ? <Medal className="w-5 h-5" style={{ color: rc }} /> :
                       rank === 3 ? <Award className="w-5 h-5" style={{ color: rc }} /> :
                       <span className="font-game text-sm" style={{ color: rc }}>{rank}</span>}
                    </div>
                    <div className="col-span-4 flex items-center gap-2">
                      <div className="w-6 h-8 rounded" style={{ backgroundColor: score.skin_color || '#FF3B30' }} />
                      <div>
                        <button
                          onClick={() => onViewProfile && onViewProfile(score.wallet_address)}
                          disabled={!score.wallet_address}
                          className={`font-mono text-sm text-left hover:underline transition-colors ${isMe ? 'text-[#14F195]' : 'text-white hover:text-[#14F195]'} disabled:cursor-default disabled:no-underline`}
                          data-testid={`leaderboard-player-link-${rank}`}>
                          {formatAddress(score.wallet_address)}
                        </button>
                        {isMe && <span className="text-[#14F195] text-xs font-game block">YOU</span>}
                      </div>
                    </div>
                    <div className="col-span-3 flex items-center justify-end">
                      <span className="font-game text-lg" style={{ color: rank <= 3 ? rc : '#fff' }}>{score.score?.toLocaleString()}</span>
                    </div>
                    <div className="col-span-2 flex items-center justify-end">
                      <span className="text-[#9945FF] font-game text-sm">{score.level}</span>
                    </div>
                    <div className="col-span-2 flex items-center justify-end gap-1">
                      <Target className="w-4 h-4 text-[#FF3B30]" />
                      <span className="text-[#FF3B30] font-game text-sm">{score.kills}</span>
                    </div>
                  </div>
                );
              })}
            </ScrollArea>
          </div>
        )}

        {/* === HISTORY VIEW === */}
        {!loading && activeView === 'history' && (
          <div className="glass-card rounded-xl overflow-hidden">
            <div className="grid grid-cols-12 gap-2 p-4 border-b border-[#27272a] bg-[#5AC8FA10]">
              <div className="col-span-3 text-[#A1A1AA] font-game text-xs">PLAYER</div>
              <div className="col-span-2 text-[#A1A1AA] font-game text-xs text-right">SCORE</div>
              <div className="col-span-2 text-[#A1A1AA] font-game text-xs text-right">MODE</div>
              <div className="col-span-1 text-[#A1A1AA] font-game text-xs text-right">LVL</div>
              <div className="col-span-1 text-[#A1A1AA] font-game text-xs text-right">KILLS</div>
              <div className="col-span-3 text-[#A1A1AA] font-game text-xs text-right">DATE</div>
            </div>
            <ScrollArea className="h-[400px]">
              {historyScores.length === 0 ? (
                <div className="p-8 text-center">
                  <History className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                  <p className="text-[#A1A1AA]">No game history yet.</p>
                </div>
              ) : historyScores.map((s, i) => {
                const mode = GAME_MODES.find(m => m.id === s.game_mode) || GAME_MODES[0];
                const isMe = s.wallet_address === currentWallet;
                return (
                  <div key={i} className={`grid grid-cols-12 gap-2 p-3 border-b border-[#1a1a1a] text-sm ${isMe ? 'bg-[#14F195]/10' : 'hover:bg-[#111]'}`}>
                    <div className="col-span-3 flex items-center">
                      <button
                        onClick={() => onViewProfile && onViewProfile(s.wallet_address)}
                        disabled={!s.wallet_address}
                        className={`font-mono text-xs text-left hover:underline transition-colors ${isMe ? 'text-[#14F195]' : 'text-white hover:text-[#14F195]'} disabled:cursor-default disabled:no-underline`}
                        data-testid={`history-player-link-${i}`}>
                        {formatAddress(s.wallet_address)}
                      </button>
                    </div>
                    <div className="col-span-2 text-right font-game text-[#FFD700]">{s.score?.toLocaleString()}</div>
                    <div className="col-span-2 text-right">
                      <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: `${mode.color}20`, color: mode.color }}>{mode.name}</span>
                    </div>
                    <div className="col-span-1 text-right text-[#9945FF] font-game text-xs">{s.level}</div>
                    <div className="col-span-1 text-right text-[#FF3B30] font-game text-xs">{s.kills}</div>
                    <div className="col-span-3 text-right text-[#666] text-xs">{formatDate(s.created_at)}</div>
                  </div>
                );
              })}
            </ScrollArea>
            {/* Pagination */}
            {historyTotal > 30 && (
              <div className="flex items-center justify-between p-3 border-t border-[#27272a]">
                <Button onClick={() => setHistoryOffset(Math.max(0, historyOffset - 30))} disabled={historyOffset === 0}
                  className="bg-[#333] text-xs hover:bg-[#444]">Prev</Button>
                <span className="text-[#666] text-xs">{historyOffset + 1}-{Math.min(historyOffset + 30, historyTotal)} of {historyTotal}</span>
                <Button onClick={() => setHistoryOffset(historyOffset + 30)} disabled={historyOffset + 30 >= historyTotal}
                  className="bg-[#333] text-xs hover:bg-[#444]">Next</Button>
              </div>
            )}
          </div>
        )}

        {/* === CHAMPIONS VIEW === */}
        {!loading && activeView === 'champions' && (
          <div className="glass-card rounded-xl overflow-hidden">
            <div className="p-4 border-b border-[#27272a] bg-[#FF950010]">
              <h2 className="font-game text-sm text-[#FF9500] flex items-center gap-2"><Crown className="w-4 h-4" /> TOURNAMENT CHAMPIONS</h2>
            </div>
            <ScrollArea className="h-[400px]">
              {champions.length === 0 ? (
                <div className="p-8 text-center">
                  <Crown className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                  <p className="text-[#A1A1AA]">No tournament champions yet.</p>
                  <p className="text-[#52525B] text-sm mt-1">Win a tournament to appear here!</p>
                </div>
              ) : champions.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-4 border-b border-[#1a1a1a] hover:bg-[#111]"
                  data-testid={`champion-${i}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#FFD700]/20 flex items-center justify-center">
                      <Crown className="w-5 h-5 text-[#FFD700]" />
                    </div>
                    <div>
                      <button
                        onClick={() => onViewProfile && onViewProfile(c.champion_id)}
                        disabled={!c.champion_id}
                        className="font-game text-sm text-white hover:text-[#FFD700] hover:underline transition-colors text-left disabled:cursor-default disabled:no-underline"
                        data-testid={`champion-link-${i}`}>
                        {c.champion_name}
                      </button>
                      <p className="text-xs text-[#A1A1AA]">{c.tournament_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-game text-sm text-[#FFD700]">{c.final_score?.toLocaleString() || '-'}</p>
                    <div className="flex items-center gap-2 text-[10px] text-[#666]">
                      <span>{c.bracket_size} players</span>
                      <span>{c.game_mode}</span>
                    </div>
                  </div>
                </div>
              ))}
            </ScrollArea>
          </div>
        )}

        {/* === STATS VIEW === */}
        {!loading && activeView === 'stats' && (
          <div className="space-y-6">
            {/* Overall stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="glass-card p-4 text-center">
                <p className="text-[#A1A1AA] text-xs mb-1">TOTAL GAMES</p>
                <p className="font-game text-2xl text-[#14F195]">{overallStats.total_games?.toLocaleString() || 0}</p>
              </div>
              <div className="glass-card p-4 text-center">
                <p className="text-[#A1A1AA] text-xs mb-1">TOTAL KILLS</p>
                <p className="font-game text-2xl text-[#FF3B30]">{overallStats.total_kills?.toLocaleString() || 0}</p>
              </div>
              <div className="glass-card p-4 text-center">
                <p className="text-[#A1A1AA] text-xs mb-1">GAME MODES</p>
                <p className="font-game text-2xl text-[#FFD700]">{overallStats.game_modes || 0}</p>
              </div>
            </div>

            {/* Per-mode stats */}
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="p-4 border-b border-[#27272a] bg-[#14F19510]">
                <h2 className="font-game text-sm text-[#14F195]">STATS BY MODE</h2>
              </div>
              {modeStats.length === 0 ? (
                <div className="p-8 text-center">
                  <TrendingUp className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                  <p className="text-[#A1A1AA]">No stats available yet.</p>
                </div>
              ) : (
                <div className="divide-y divide-[#1a1a1a]">
                  {modeStats.map((stat, i) => {
                    const mode = GAME_MODES.find(m => m.id === stat.game_mode) || { name: stat.game_mode, color: '#A1A1AA', icon: Gamepad2 };
                    const ModeIcon = mode.icon;
                    return (
                      <div key={i} className="p-4 hover:bg-[#111]">
                        <div className="flex items-center gap-3 mb-3">
                          <ModeIcon className="w-5 h-5" style={{ color: mode.color }} />
                          <span className="font-game text-sm" style={{ color: mode.color }}>{mode.name}</span>
                        </div>
                        <div className="grid grid-cols-5 gap-3 text-center">
                          <div>
                            <p className="text-[10px] text-[#666]">GAMES</p>
                            <p className="font-game text-sm text-white">{stat.total_games}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-[#666]">HIGH SCORE</p>
                            <p className="font-game text-sm text-[#FFD700]">{stat.highest_score?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-[#666]">AVG SCORE</p>
                            <p className="font-game text-sm text-[#A1A1AA]">{stat.avg_score?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-[#666]">KILLS</p>
                            <p className="font-game text-sm text-[#FF3B30]">{stat.total_kills?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-[#666]">PLAYERS</p>
                            <p className="font-game text-sm text-[#14F195]">{stat.unique_players}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
